import { useCallback } from 'react';
import { Message } from '../../../types/chat';
import { getChatHistory } from '../../../services/chatService';
import { useScrollToBottom } from './useScrollToBottom';
import { useRetry } from './useRetry';
import { useSSEMessageHandler } from './useSSEMessageHandler';
import { useMessageUpdater } from './useMessageUpdater';
import { useSuggestedQuestions } from './useSuggestedQuestions';
import { useErrorHandler } from './useErrorHandler';
import { formatHistoryMessage } from '../utils/messageFormatters';
import { CHAT_CONSTANTS } from '../constants';
import { isValidThreadId, isValidMessageText } from '../utils/validators';

interface UseMessageHandlingProps {
    // 会话ID
    thread_id: string;
    // 用户账号ID
    account_id: string;
    // 是否正在加载中
    isLoading: boolean;
    // 当前助手消息的引用
    currentAssistantMessage: React.RefObject<string>;
    // 创建SSE连接的函数
    createConnection: (text: string, account_id: string, thread_id: string, props: any) => boolean;
    // 设置消息列表的函数
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    // 设置错误信息的函数
    setError: React.Dispatch<React.SetStateAction<string | null>>;
    // 设置加载状态的函数
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    // 设置建议问题的函数
    setSuggestedQuestions: React.Dispatch<React.SetStateAction<any>>;
    // 设置建议问题加载状态的函数
    setIsLoadingSuggestions: React.Dispatch<React.SetStateAction<boolean>>;
    // 更新分页信息的函数
    updatePagination: (total: number) => void;
    // 是否已初始化
    isInitialized: boolean;
    // 标记为已初始化的函数
    markInitialized: () => void;
}

export const useMessageHandling = ({
    thread_id,
    account_id,
    isLoading,
    currentAssistantMessage,
    createConnection,
    setMessages,
    setError,
    setIsLoading,
    setSuggestedQuestions,
    setIsLoadingSuggestions,
    updatePagination,
    isInitialized,
    markInitialized,
}: UseMessageHandlingProps) => {
    const { scrollToBottom } = useScrollToBottom();
    const { handleError, clearError, setErrorMessage } = useErrorHandler({ setError });
    const { executeWithRetry } = useRetry({ onError: handleError });
    const { handleMessage } = useSSEMessageHandler({ setMessages, currentAssistantMessage });
    const {
        addUserMessage,
        addInitialAssistantMessage,
        addHistoryMessages,
    } = useMessageUpdater({ setMessages, currentAssistantMessage });
    const { fetchSuggestedQuestions, clearSuggestedQuestions } = useSuggestedQuestions({
        thread_id,
        account_id,
        setSuggestedQuestions,
        setIsLoadingSuggestions
    });

    // 发送消息
    const handleSendMessage = useCallback(async (text: string) => {
        if (!isValidMessageText(text) || isLoading || !isValidThreadId(thread_id)) {
            if (!thread_id) setErrorMessage('会话ID不存在');
            return;
        }

        try {
            setIsLoading(true);
            clearError();
            clearSuggestedQuestions();

            addUserMessage(text);
            const tempAssistantMessageId = Date.now().toString();
            addInitialAssistantMessage(tempAssistantMessageId);

            let hasReceivedData = false;
            let lastMessageType: 'text' | 'ui' | null = null;

            createConnection(text, account_id, thread_id, {
                onMessage: (event: MessageEvent<any>) => {
                    hasReceivedData = true;
                    lastMessageType = handleMessage(event, tempAssistantMessageId) as 'text' | 'ui' | null;
                },
                onClose: () => {
                    if (hasReceivedData) {
                        fetchSuggestedQuestions(lastMessageType);
                    }
                },
                onError: (event: any) => {
                    if (hasReceivedData) {
                        fetchSuggestedQuestions(lastMessageType);
                        return;
                    }
                    if (event?.target?.readyState === EventSource.CLOSED) return;
                    setErrorMessage('连接失败，请重试');
                },
                setError: handleError,
                setIsLoading
            });
        } catch (error) {
            setIsLoading(false);
            handleError(error, '发送消息失败');
        }
    }, [
        thread_id,
        account_id,
        isLoading,
        createConnection,
        handleMessage,
        addUserMessage,
        addInitialAssistantMessage,
        clearError,
        clearSuggestedQuestions,
        fetchSuggestedQuestions,
        handleError,
        setErrorMessage,
        setIsLoading
    ]);

    // 加载历史消息
    const retryLoadHistory = useCallback(async (page: number = 1, append: boolean = false) => {
        if (!thread_id || (isInitialized && !append)) return;

        const loadHistory = async () => {
            clearError();
            setIsLoading(true);

            try {
                const result = await getChatHistory(account_id, thread_id, page, CHAT_CONSTANTS.PAGE_SIZE, -1);
                const formattedMessages = result.messages.map(formatHistoryMessage).reverse();

                addHistoryMessages(formattedMessages, append);

                if (!append) {
                    if (formattedMessages.length === 0) {
                        setTimeout(() => handleSendMessage(CHAT_CONSTANTS.INITIAL_MESSAGE), 0);
                    } else {
                        const lastMessage = formattedMessages[formattedMessages.length - 1];
                        if (lastMessage.role === 'assistant') {
                            setTimeout(() => {
                                const messageType = lastMessage.type === 'ui' ? 'ui' : 'text';
                                fetchSuggestedQuestions(messageType);
                                scrollToBottom();
                            }, 200);
                        }
                    }
                    markInitialized();
                }

                updatePagination(result.total);
                return result;
            } finally {
                setIsLoading(false);
            }
        };

        await executeWithRetry(loadHistory, '加载聊天历史失败');
    }, [
        thread_id,
        isInitialized,
        handleSendMessage,
        fetchSuggestedQuestions,
        scrollToBottom,
        executeWithRetry,
        updatePagination,
        markInitialized,
        addHistoryMessages,
        clearError,
        setIsLoading
    ]);

    return {
        handleSendMessage,
        retryLoadHistory,
    };
};